import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import { Button } from 'ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'ui/dialog';

import { useConfirmationStore } from './confirmation.store';

const variantStyles: Record<string, string> = {
  destructive: 'bg-destructive',
  warning: 'bg-warning',
  default: 'bg-status-neutral',
};

const dialogContentStyles = cva('p-0 gap-0 rounded border-none', {
  variants: {
    size: {
      sm: 'sm:max-w-[327px]',
      md: 'sm:max-w-[416px]',
      lg: 'sm:max-w-[556px] ',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const dialogHeaderStyles = cva('px-6 py-4 relative border-b-[2px] border-b-border-divider', {
  variants: {
    size: {
      sm: 'px-4 py-3.5',
      md: 'px-5 py-3',
      lg: 'px-6 py-4',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

const footerStyles = cva('pt-4.5 pb-[15px] w-full text-xs text-on-color ', {
  variants: {
    size: {
      sm: '',
      md: '',
      lg: 'pb-[23px]',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export const ConfirmationDialog = () => {
  const {
    open,
    title,
    description,
    cancelLabel,
    content,
    actionLabel,
    onAction,
    closeConfirmation,
    size,
    variant = 'destructive',
    isSubmitting = null,
    onCancel,
    setSubmitting,
  } = useConfirmationStore();

  const showCancelButton = !!cancelLabel;
  const showActionButton = !!actionLabel;

  const handleAction = async () => {
    try {
      setSubmitting('submit');
      await Promise.resolve(onAction());
      closeConfirmation();
    } catch {
      // The action handles its own user-facing error state.
    } finally {
      setSubmitting(null);
    }
  };

  const handleCancel = async () => {
    try {
      setSubmitting('cancel');
      await Promise.resolve(onCancel?.());
      closeConfirmation();
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isSubmitting === null) closeConfirmation();
        }}
      >
        <DialogContent className={dialogContentStyles({ size })}>
          <DialogHeader className={dialogHeaderStyles({ size })}>
            <div className="flex justify-between items-center">
              <DialogTitle className="text-base">{title}</DialogTitle>
              <div className="flex gap-3">
                {showCancelButton && (
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => {
                      void handleCancel();
                    }}
                    isLoading={isSubmitting === 'cancel'}
                    disabled={isSubmitting === 'cancel'}
                    className="bg-card text-muted-foreground"
                  >
                    {cancelLabel}
                  </Button>
                )}
                {showActionButton && (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => void handleAction()}
                    isLoading={isSubmitting === 'submit'}
                    disabled={isSubmitting === 'submit'}
                  >
                    {actionLabel}
                  </Button>
                )}
              </div>
            </div>
            <DialogClose className="absolute -top-10 left-4"></DialogClose>
          </DialogHeader>
          <div className="flex items-center space-x-2"></div>
          {description && (
            <DialogFooter className={footerStyles({ size })}>
              <div className={cn('py-3 px-6 w-full text-xs text-on-color', variantStyles[variant])}>{description}</div>
            </DialogFooter>
          )}

          <DialogDescription className="sr-only">{title}</DialogDescription>
          {content && <div>{content}</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
};
